import { TRequest, TPharmacyOrder, TPharmacyOrderStatus, TDocumentTemplate, TNewDocumentTemplate, TDocument, TDocumentContent, TSignaturePosition, TAdminClientData, TAdminClientActivity } from "@shared/entities";
import { useState } from "react";
import {
	TProfile,
	TRegisterForm,
	TUser,
	TNewClient,
	TLead,
	TNewLead,
	TTask,
	TNewTask,
	TAutomationRule,
	TNewAutomationRule,
} from "@shared/entities";
import { TDoctor, TNewDoctor } from "@shared/entities/doctor";
import { TBroker, TNewBroker } from "@shared/entities/broker";
import { firebaseApi, LogPayload } from "src/firebase-api";
import { useStore } from "src/store/store";
import { SubNestedKeys } from "src/types";
import { useLocation } from "wouter";
import { FirebaseError } from "firebase/app";
import { arrayUnion, deleteField, doc, getFirestore, updateDoc } from "firebase/firestore";
import { app } from "src/firebase-api/firebaseApp";
import { useAdminStore } from "src/store/adminStore";
import { analytics } from "src/infra/analytics/analytics";
import { TRequestActivity } from "@shared/entities/requestActivity";
import { CONFIG } from "src/config";
import { RecaptchaVerifier } from "firebase/auth";
import { approvalFieldUpdates, profileCopyPath, TClientDocuments, TClientDocumentType } from "src/utils/clientDocuments";
import { retryColdStart } from "src/utils/retryColdStart";

type Path = string; // todo

function logger(payload: LogPayload) {
	if (CONFIG.IS_DEV) {
		console.log(payload);
	}
	firebaseApi.api.uiLogs(payload);
}

export const useAppApi = () => {
	const store = useStore();

	const adminStore = useAdminStore();

	console.log(store);

	const { user } = store;
	const [loading, setLoading] = useState<
		Partial<Record<SubNestedKeys<Omit<typeof api, "loading">>, boolean | undefined>>
	>({});

	const [, _navigate] = useLocation();

	function navigate(path: Path) {
		_navigate(path);
	}

	/** Back buttons: return to the previous page in the tab's history (like the
	 *  browser's back button), so the user lands exactly where they came from.
	 *  When there is no history (page opened directly from a link), go to
	 *  `fallback` instead. */
	function goBack(fallback: Path) {
		if (window.history.length > 1) {
			window.history.back();
		} else {
			navigate(fallback);
		}
	}

	const isValidUser = !!user;

	const api = {
		client: {
			openCurrentRequest() {
				navigate("/new_request");
			},
			async updateProfile(profile: TProfile) {
				const res = await firebaseApi.api.updateClientProfile(profile.id, profile);
				store.storeApi.setProfile(res);
				return { success: true, request: res };
			},
			async getActiveRequest() {
				if (!isValidUser) return [];

				setLoading({
					...loading,
					"client.getRequests": true,
				});
				const res = await firebaseApi.firestore.list<TRequest>({
					collectionName: "requests",
					filter: [
						{ name: "userId", operator: "==", value: user.uid },
						{
							name: "status",
							operator: "not-in",
							value: ["completed", "rejected"],
						},
					],
				});

				setLoading({
					...loading,
					"client.getRequests": true,
				});

				return res as TRequest[];
			},
			async getRequests() {
				if (!isValidUser) return [];

				setLoading({
					...loading,
					"client.getRequests": true,
				});
				const res = await firebaseApi.firestore.list<TRequest>({
					collectionName: "requests",
					filter: [{ name: "userId", operator: "==", value: user.uid }],
				});

				setLoading({
					...loading,
					"client.getRequests": true,
				});

				return res as TRequest[];
			},
			async setRequest(data: Partial<TRequest>) {
				const res = await firebaseApi.firestore.set({
					collectionName: "requests",
					item: data,
				});
				return { success: true, request: res };
			},
			async createPharmacyOrder(orderData: {
				items: Array<{
					product: any;
					quantity: number;
				}>;
				deliveryDetails: {
					fullName: string;
					phone: string;
					address: string;
					city: string;
					postalCode: string;
					deliveryNotes: string;
				};
				total: number;
				coinsToUse?: number;
				userId?: string; // Optional: for admin creating orders for clients
				userEmail?: string; // Optional: for admin creating orders for clients
			}) {
				setLoading({
					...loading,
					"client.createPharmacyOrder": true,
				});

				try {
					const coinsToUse = orderData.coinsToUse ?? 0;

					if (coinsToUse > 0) {
						// Use callable to atomically deduct coins + create order
						const res = await firebaseApi.api.createPharmacyOrderWithCoins({
							items: orderData.items,
							deliveryDetails: orderData.deliveryDetails,
							total: orderData.total,
							coinsToUse,
							userId: orderData.userId,
							userEmail: orderData.userEmail || user?.email || "",
						});

						setLoading({ ...loading, "client.createPharmacyOrder": false });

						if (res.success && !orderData.userId && user?.uid) {
							// Refresh profile so coins balance is up to date
							const updatedProfile = await firebaseApi.firestore.get<import("@shared/entities").TProfile>({
								collectionName: "profiles",
								id: user.uid,
							});
							if (updatedProfile) {
								store.storeApi.setProfile(updatedProfile);
							}
						}

						return res;
					}

					// Create order document directly (no coins)
					const order = {
						id: "", // Will be set by Firestore
						userId: orderData.userId || user?.uid || "",
						userEmail: orderData.userEmail || user?.email || "",
						items: orderData.items,
						deliveryDetails: orderData.deliveryDetails,
						total: orderData.total,
						status: "pending",
						createdAt: Date.now(),
						updatedAt: Date.now(),
						coinsAwarded: false,
					};

					const created = await firebaseApi.firestore.create({
						collectionName: "pharmacy_orders",
						item: order,
					});

					setLoading({
						...loading,
						"client.createPharmacyOrder": false,
					});

					return { success: true, order: created };
				} catch (error: any) {
					setLoading({
						...loading,
						"client.createPharmacyOrder": false,
					});
					return { success: false, error: error.message };
				}
			},
			requestPhoneVerificationCode: async (
				phone: string,
				recaptchaVerifier: RecaptchaVerifier,
			) => {
				try {
					setLoading({
						...loading,
						"client.requestPhoneVerificationCode": true,
					});
					const verificationId = await firebaseApi.auth.verifyPhoneNumber(
						phone,
						recaptchaVerifier,
					);
					logger({
						message: `phone verification code sent (${phone})`,
						severity: "INFO",
						phone,
					});
					setLoading({
						...loading,
						"client.requestPhoneVerificationCode": false,
					});
					return { success: true, verificationId, error: null };
				} catch (error: any) {
					logger({
						message: `phone verification code request error (${error.message})`,
						severity: "ERROR",
						phone,
						error: error as FirebaseError,
					});
					setLoading({
						...loading,
						"client.requestPhoneVerificationCode": false,
					});
					return { success: false, verificationId: null, error: error as FirebaseError };
				}
			},
			linkPhoneToUser: async (user: TUser, verificationId: string, code: string) => {
				try {
					const linkedUser = await firebaseApi.auth.linkPhoneCredential(
						user,
						verificationId,
						code,
					);
					logger({
						message: `phone linked to user successfully`,
						severity: "INFO",
						userId: linkedUser.uid,
					});
					return { success: true, user: linkedUser, error: null };
				} catch (error: any) {
					logger({
						message: `phone link error (${error.message})`,
						severity: "ERROR",
						error: error as FirebaseError,
					});
					return { success: false, user: null, error: error as FirebaseError };
				}
			},
			async register(form: TRegisterForm, verificationId?: string, phoneCode?: string) {
				let pendingUser: TUser | undefined;
				let profileCommitted = false;
				let completionAttempted = false;
				setLoading({ ...loading, "client.register": true });
				try {
					const { password, terms: _terms, ...profileData } = form;
					// Retried only for the cold-start failure (see retryColdStart).
					pendingUser = await retryColdStart(() => firebaseApi.auth.register({ email: form.email, password }));
					if (verificationId && phoneCode && !pendingUser.phoneNumber) {
						pendingUser = await firebaseApi.auth.linkPhoneCredential(pendingUser, verificationId, phoneCode);
					}
					completionAttempted = true;
					const profile = await firebaseApi.auth.completeRegistration(profileData);
					profileCommitted = true;
					const { user: finalUser } = await firebaseApi.auth.login(form.email, password);
					await firebaseApi.auth.endRegistration();
					analytics.track("SIGNUP", profile);
					store.storeApi.setUser(finalUser);
					store.storeApi.setProfile(profile);
					_navigate("/");
					return { user: finalUser, profile, success: true };
				} catch (error: any) {
					// Logged: a failed signup used to leave no trace anywhere.
					logger({
						message: `register error (${error?.code ?? error?.message})`,
						severity: "ERROR",
						step: completionAttempted ? "complete-profile" : "account-or-phone-link",
						code: error?.code,
					});
					// Never delete after an ambiguous network failure: completion may
					// have committed. The separate session can safely resume on retry.
					const rejected = ["functions/already-exists", "functions/invalid-argument", "functions/failed-precondition"].includes(error?.code);
					if (pendingUser && !profileCommitted && (!completionAttempted || rejected)) {
						await firebaseApi.auth.discardRegistration(pendingUser).catch(console.error);
					}
					return { success: false, error };
				} finally {
					setLoading({ ...loading, "client.register": false });
				}
			},
		},

		admin: {
			async rejectRequest({
				id,
				adminMessageForClient,
			}: {
				id: string;
				adminMessageForClient?: string;
			}) {
				setLoading({
					...loading,
					"admin.rejectRequest": true,
				});

				const res = await firebaseApi.firestore.set<Partial<TRequest>>({
					collectionName: "requests",
					item: {
						id,
						status: "rejected",
						adminMessageForClient,
					},
				});

				setLoading({
					...loading,
					"admin.rejectRequest": false,
				});

				return { success: true, data: res as TRequest };
			},
			/** Approve a request and hand its documents to the client.
			 *
			 *  The files attached in the approval dialog are saved here and only here
			 *  — never on upload — so the client cannot see them before the admin
			 *  approves. Each type is written by its own field path, so a type the
			 *  dialog did not touch is never overwritten; a type is removed only when
			 *  the admin removed a document that was saved before.
			 *
			 *  Then the files are copied into the client's profile, each to its own
			 *  path under profiles/{id} (the license a pharmacy order reads lives
			 *  there). The request is the record and is written first; the copy is a
			 *  convenience, so if it fails the approval stands and the client still
			 *  has the documents on the request. `profileCopy` tells the caller which
			 *  of these happened; `success: false` means the request was NOT saved. */
			async approveRequest({
				id,
				userId,
				documentsForClient,
				savedDocumentsForClient,
			}: {
				id: string;
				userId?: string;
				documentsForClient: TClientDocuments;
				savedDocumentsForClient: TClientDocuments;
			}): Promise<
				| {
						success: true;
						profileCopy: "copied" | "no-profile" | "nothing-to-copy" | "failed";
						/** Saved on the request but not copied: they fail the profile's document check. */
						notCopied: TClientDocumentType[];
				  }
				| { success: false; error: unknown }
			> {
				setLoading({
					...loading,
					"admin.approveRequest": true,
				});

				const fields = approvalFieldUpdates(documentsForClient, savedDocumentsForClient, deleteField);
				try {
					await firebaseApi.firestore.updateAtomically([
						{ collectionName: "requests", id, updates: { status: "completed", ...fields.request } },
					]);
				} catch (error) {
					console.error("[approveRequest] failed", error);
					setLoading({ ...loading, "admin.approveRequest": false });
					return { success: false, error };
				}

				// From here on the request IS approved. Whatever fails below is
				// reported as a failed copy, never as a failed approval — a caller
				// told "nothing was saved" would discard files the request now uses.
				const notCopied = fields.notCopied;
				const toCopy = Object.entries(fields.profile) as Array<[string, TDocument]>;
				// Copies made so far. If the copy fails part-way, nothing will ever
				// point at them — not the profile, and not request deletion, which
				// only clears requests/{id} — so they are removed again.
				const created: string[] = [];
				try {
					if (toCopy.length === 0) return { success: true, profileCopy: "nothing-to-copy", notCopied };
					// A request filed without a profile (e.g. by a broker) has no one to
					// copy to; the documents stay on the request.
					const profile = userId
						? await firebaseApi.firestore.get<TProfile>({ collectionName: "profiles", id: userId })
						: null;
					if (!profile) return { success: true, profileCopy: "no-profile", notCopied };

					// The profile gets its own copy of each file: deleting the request
					// deletes everything under requests/{id}, and must not take the
					// profile's license with it.
					const profileUpdates: Record<string, TDocument> = {};
					for (const [field, document] of toCopy) {
						const path = profileCopyPath(profile.id, document);
						// Registered before the copy starts: a copy that fails half-way
						// may already exist (removing a path that does not is harmless).
						created.push(path);
						const url = await firebaseApi.storage.copyFile(document.path, path, document.fileType);
						profileUpdates[field] = { ...document, path, url };
					}
					await firebaseApi.firestore.updateAtomically([
						{ collectionName: "profiles", id: profile.id, updates: profileUpdates },
					]);
					return { success: true, profileCopy: "copied", notCopied };
				} catch (error) {
					console.error("[approveRequest] approved, but copying documents to the profile failed", error);
					await Promise.all(created.map((path) => firebaseApi.storage.remove(path)));
					return { success: true, profileCopy: "failed", notCopied };
				} finally {
					setLoading({ ...loading, "admin.approveRequest": false });
				}
			},
			async deleteRequest({ id }: { id: string }) {
				setLoading({
					...loading,
					"admin.deleteRequest": true,
				});

				// Delete the request document - Firebase Storage cleanup will be handled by Cloud Function
				const res = await firebaseApi.firestore.delete({
					collectionName: "requests",
					id,
				});

				setLoading({
					...loading,
					"admin.deleteRequest": false,
				});

				return { success: true, data: res };
			},
			async saveRequest(data: TRequest) {
				const res = await firebaseApi.firestore.set({
					collectionName: "requests",
					item: data,
				});
				return { success: true, request: res };
			},
			async setRequest(data: Partial<TRequest>) {
				const res = await firebaseApi.firestore.set({
					collectionName: "requests",
					item: data,
				});
				return { success: true, request: res };
			},
			async requestExternalDocuments({
				id,
				adminMessage,
			}: {
				id: string;
				adminMessage?: string;
			}) {
				setLoading({
					...loading,
					"admin.requestExternalDocuments": true,
				});

				const res = await firebaseApi.firestore.set<Partial<TRequest>>({
					collectionName: "requests",
					item: { id, status: "pending_external_documents", adminMessage },
				});

				setLoading({
					...loading,
					"admin.requestExternalDocuments": false,
				});

				return { success: true, data: res as TRequest };
			},
			async requestChangesFromBroker({
				id,
				adminMessageForBroker,
			}: {
				id: string;
				adminMessageForBroker?: string;
			}) {
				setLoading({
					...loading,
					"admin.requestChangesFromBroker": true,
				});

				const res = await firebaseApi.firestore.set<Partial<TRequest>>({
					collectionName: "requests",
					item: { id, status: "change_required_broker", adminMessageForBroker },
				});

				setLoading({
					...loading,
					"admin.requestChangesFromBroker": false,
				});

				return { success: true, data: res as TRequest };
			},
			async getRequestActivity(id: string) {
				setLoading({
					...loading,
					"admin.getRequestActivity": true,
				});
				const res = await firebaseApi.firestore.list({
					collectionName: `requests/${id}/activity` as any,
				});

				setLoading({
					...loading,
					"admin.getRequestActivity": false,
				});

				return { success: true, data: res as TRequestActivity[] };
			},
			async getRequest(id: string) {
				setLoading({
					...loading,
					"admin.getRequest": true,
				});
				const res = await firebaseApi.firestore.get({
					collectionName: "requests",
					id,
				});

				setLoading({
					...loading,
					"admin.getRequest": false,
				});

				return { success: true, data: res as TRequest };
			},
			async createDoctor(doctor: TNewDoctor) {
				try {
					setLoading({
						...loading,
						"admin.createDoctor": true,
					});
					const response = await firebaseApi.api.createDoctor(doctor);
					console.log("response", response);

					if (response.success && response.data) {
						adminStore.setDoctors([...adminStore.doctors, response.data]);
					}

					setLoading({
						...loading,
						"admin.createDoctor": false,
					});

					return response;
				} catch (error: any) {
					console.error(error.message);
					setLoading({
						...loading,
						"admin.createDoctor": false,
					});
				}
			},
			async getDoctors() {
				setLoading({
					...loading,
					"admin.getDoctors": true,
				});
				const doctors = await firebaseApi.firestore.list<TDoctor>({
					collectionName: "doctors",
				});

				console.log("doctors", doctors);

				adminStore.setDoctors(doctors);

				setLoading({
					...loading,
					"admin.getDoctors": false,
				});

				return { success: true, data: doctors };
			},
			async deleteDoctor(id: string) {
				try {
					setLoading({
						...loading,
						"admin.deleteDoctor": true,
					});
					const response = await firebaseApi.api.deleteDoctor(id);
					console.log("deleteDoctor response", response);

					if (response.success) {
						// Remove doctor from store
						adminStore.removeDoctor(id);
					}

					setLoading({
						...loading,
						"admin.deleteDoctor": false,
					});

					return response;
				} catch (error: any) {
					console.error(error.message);
					setLoading({
						...loading,
						"admin.deleteDoctor": false,
					});
					return { success: false, error: error.message };
				}
			},
			async resetDoctorPassword(id: string) {
				try {
					setLoading({
						...loading,
						"admin.resetDoctorPassword": true,
					});
					const response = await firebaseApi.api.resetDoctorPassword(id);
					console.log("resetDoctorPassword response", response);

					setLoading({
						...loading,
						"admin.resetDoctorPassword": false,
					});

					return response;
				} catch (error: any) {
					console.error(error.message);
					setLoading({
						...loading,
						"admin.resetDoctorPassword": false,
					});
					return { success: false, error: error.message };
				}
			},
			async createBroker(broker: TNewBroker) {
				try {
					setLoading({
						...loading,
						"admin.createBroker": true,
					});
					const response = await firebaseApi.api.createBroker(broker);
					console.log("response", response);

					if (response.success && response.data) {
						adminStore.setBrokers([...adminStore.brokers, response.data]);
					}

					setLoading({
						...loading,
						"admin.createBroker": false,
					});

					return response;
				} catch (error: any) {
					console.error(error.message);
					setLoading({
						...loading,
						"admin.createBroker": false,
					});
				}
			},
			async getBrokers() {
				setLoading({
					...loading,
					"admin.getBrokers": true,
				});
				const brokers = await firebaseApi.firestore.list<TBroker>({
					collectionName: "brokers",
				});

				console.log("brokers", brokers);

				adminStore.setBrokers(brokers);

				setLoading({
					...loading,
					"admin.getBrokers": false,
				});

				return { success: true, data: brokers };
			},
			async deleteBroker(id: string) {
				try {
					setLoading({
						...loading,
						"admin.deleteBroker": true,
					});
					const response = await firebaseApi.api.deleteBroker(id);
					console.log("deleteBroker response", response);

					if (response.success) {
						// Remove broker from store
						adminStore.removeBroker(id);
					}

					setLoading({
						...loading,
						"admin.deleteBroker": false,
					});

					return response;
				} catch (error: any) {
					console.error(error.message);
					setLoading({
						...loading,
						"admin.deleteBroker": false,
					});
					return { success: false, error: error.message };
				}
			},
			async resetBrokerPassword(id: string) {
				try {
					setLoading({
						...loading,
						"admin.resetBrokerPassword": true,
					});
					const response = await firebaseApi.api.resetBrokerPassword(id);
					console.log("resetBrokerPassword response", response);

					setLoading({
						...loading,
						"admin.resetBrokerPassword": false,
					});

					return response;
				} catch (error: any) {
					console.error(error.message);
					setLoading({
						...loading,
						"admin.resetBrokerPassword": false,
					});
					return { success: false, error: error.message };
				}
			},
			async createClient(client: TNewClient) {
				try {
					setLoading({
						...loading,
						"admin.createClient": true,
					});
					const response = await firebaseApi.api.createClient(client);

					setLoading({
						...loading,
						"admin.createClient": false,
					});

					return response;
				} catch (error: any) {
					console.error(error.message);
					setLoading({
						...loading,
						"admin.createClient": false,
					});
					return { success: false, error: error.message };
				}
			},
			async getClients() {
				setLoading({
					...loading,
					"admin.getClients": true,
				});
				const res = await firebaseApi.firestore.list<TProfile>({
					collectionName: "profiles",
				});

				setLoading({
					...loading,
					"admin.getClients": false,
				});

				return { success: true, data: res };
			},
			async sendClientJoinLink(clientId: string) {
				setLoading({ ...loading, "admin.sendClientJoinLink": true });
				try {
					const res = await firebaseApi.api.sendClientJoinLink(clientId);
					setLoading({ ...loading, "admin.sendClientJoinLink": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.sendClientJoinLink": false });
					return { success: false, error: error.message };
				}
			},
			async deleteClient(id: string) {
				try {
					setLoading({
						...loading,
						"admin.deleteClient": true,
					});
					const response = await firebaseApi.api.deleteClient(id);
					setLoading({
						...loading,
						"admin.deleteClient": false,
					});
					return response;
				} catch (error: any) {
					setLoading({
						...loading,
						"admin.deleteClient": false,
					});
					return { success: false, error: error.message };
				}
			},
			async getLeads() {
				setLoading({ ...loading, "admin.getLeads": true });
				try {
					const res = await firebaseApi.firestore.list<TLead>({
						collectionName: "leads",
						sort: [{ name: "createdAt", by: "desc" }],
					});
					setLoading({ ...loading, "admin.getLeads": false });
					return { success: true, data: res };
				} catch (error: any) {
					setLoading({ ...loading, "admin.getLeads": false });
					return { success: false, error: error.message, data: [] };
				}
			},
			async createLead(lead: TNewLead) {
				setLoading({ ...loading, "admin.createLead": true });
				try {
					const created = await firebaseApi.firestore.create<TLead>({
						collectionName: "leads",
						item: {
							...lead,
							id: "",
							createdAt: lead.createdAt ?? Date.now(),
						} as TLead,
					});
					setLoading({ ...loading, "admin.createLead": false });
					return { success: true, data: created };
				} catch (error: any) {
					setLoading({ ...loading, "admin.createLead": false });
					return { success: false, error: error.message };
				}
			},
			async updateLead(id: string, lead: Partial<TNewLead>) {
				setLoading({ ...loading, "admin.updateLead": true });
				try {
					await firebaseApi.firestore.update<TLead>({
						collectionName: "leads",
						item: { ...lead, id } as TLead,
					});
					setLoading({ ...loading, "admin.updateLead": false });
					return { success: true };
				} catch (error: any) {
					setLoading({ ...loading, "admin.updateLead": false });
					return { success: false, error: error.message };
				}
			},
			async deleteLead(id: string) {
				setLoading({ ...loading, "admin.deleteLead": true });
				try {
					await firebaseApi.firestore.delete({ collectionName: "leads", id });
					setLoading({ ...loading, "admin.deleteLead": false });
					return { success: true };
				} catch (error: any) {
					setLoading({ ...loading, "admin.deleteLead": false });
					return { success: false, error: error.message };
				}
			},
			async getAutomations() {
				setLoading({ ...loading, "admin.getAutomations": true });
				try {
					const res = await firebaseApi.firestore.list<TAutomationRule>({
						collectionName: "automations",
						sort: [{ name: "createdAt", by: "desc" }],
					});
					setLoading({ ...loading, "admin.getAutomations": false });
					return { success: true, data: res };
				} catch (error: any) {
					setLoading({ ...loading, "admin.getAutomations": false });
					return { success: false, error: error.message, data: [] };
				}
			},
			async createAutomation(rule: TNewAutomationRule) {
				setLoading({ ...loading, "admin.createAutomation": true });
				try {
					const created = await firebaseApi.firestore.create<TAutomationRule>({
						collectionName: "automations",
						item: { ...rule, id: "" } as TAutomationRule,
					});
					setLoading({ ...loading, "admin.createAutomation": false });
					return { success: true, data: created };
				} catch (error: any) {
					setLoading({ ...loading, "admin.createAutomation": false });
					return { success: false, error: error.message };
				}
			},
			async updateAutomation(id: string, updates: Partial<TAutomationRule>) {
				setLoading({ ...loading, "admin.updateAutomation": true });
				try {
					await firebaseApi.firestore.update<TAutomationRule>({
						collectionName: "automations",
						item: { id, ...updates, updatedAt: Date.now() } as TAutomationRule,
					});
					setLoading({ ...loading, "admin.updateAutomation": false });
					return { success: true };
				} catch (error: any) {
					setLoading({ ...loading, "admin.updateAutomation": false });
					return { success: false, error: error.message };
				}
			},
			async deleteAutomation(id: string) {
				setLoading({ ...loading, "admin.deleteAutomation": true });
				try {
					await firebaseApi.firestore.delete({ collectionName: "automations", id });
					setLoading({ ...loading, "admin.deleteAutomation": false });
					return { success: true };
				} catch (error: any) {
					setLoading({ ...loading, "admin.deleteAutomation": false });
					return { success: false, error: error.message };
				}
			},
			async getTasks() {
				setLoading({ ...loading, "admin.getTasks": true });
				try {
					const res = await firebaseApi.firestore.list<TTask>({
						collectionName: "tasks",
						sort: [{ name: "createdAt", by: "desc" }],
					});
					setLoading({ ...loading, "admin.getTasks": false });
					return { success: true, data: res };
				} catch (error: any) {
					setLoading({ ...loading, "admin.getTasks": false });
					return { success: false, error: error.message, data: [] };
				}
			},
			async getClientTasks(clientId: string) {
				setLoading({ ...loading, "admin.getClientTasks": true });
				try {
					const res = await firebaseApi.firestore.list<TTask>({
						collectionName: "tasks",
						filter: [{ name: "clientId" as const, value: clientId, operator: "==" }],
						sort: [{ name: "createdAt", by: "desc" }],
					});
					setLoading({ ...loading, "admin.getClientTasks": false });
					return { success: true, data: res };
				} catch (error: any) {
					console.error("getClientTasks error:", error?.message || error);
					setLoading({ ...loading, "admin.getClientTasks": false });
					return { success: false, error: error.message, data: [] };
				}
			},
			async parseClientPrescriptions(clientId: string) {
				setLoading({ ...loading, "admin.parseClientPrescriptions": true });
				try {
					const res = await firebaseApi.api.parsePrescriptionsDocument({ clientId });
					setLoading({ ...loading, "admin.parseClientPrescriptions": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.parseClientPrescriptions": false });
					return { success: false as const, error: error.message };
				}
			},
			async confirmClientPrescriptions(
				clientId: string,
				prescriptions: { opensAt: number; label?: string }[],
			) {
				setLoading({ ...loading, "admin.confirmClientPrescriptions": true });
				try {
					const res = await firebaseApi.api.confirmPrescriptions({ clientId, prescriptions });
					setLoading({ ...loading, "admin.confirmClientPrescriptions": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.confirmClientPrescriptions": false });
					return { success: false as const, error: error.message };
				}
			},
			async parseClientIdDocument(clientId: string) {
				setLoading({ ...loading, "admin.parseClientIdDocument": true });
				try {
					const res = await firebaseApi.api.parseIdDocument({ clientId });
					setLoading({ ...loading, "admin.parseClientIdDocument": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.parseClientIdDocument": false });
					return { success: false as const, error: error.message };
				}
			},
			async confirmClientIdDetails(clientId: string, dob: number) {
				setLoading({ ...loading, "admin.confirmClientIdDetails": true });
				try {
					const res = await firebaseApi.api.confirmIdDetails({ clientId, dob });
					setLoading({ ...loading, "admin.confirmClientIdDetails": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.confirmClientIdDetails": false });
					return { success: false as const, error: error.message };
				}
			},
			async parseClientLicense(clientId: string) {
				setLoading({ ...loading, "admin.parseClientLicense": true });
				try {
					const res = await firebaseApi.api.parseLicenseDocument({ clientId });
					setLoading({ ...loading, "admin.parseClientLicense": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.parseClientLicense": false });
					return { success: false as const, error: error.message };
				}
			},
			async confirmClientLicenseRenewal(
				clientId: string,
				expiresAt: number,
				licenseNumber?: string,
			) {
				setLoading({ ...loading, "admin.confirmClientLicenseRenewal": true });
				try {
					const res = await firebaseApi.api.confirmLicenseRenewal({
						clientId,
						expiresAt,
						licenseNumber,
					});
					setLoading({ ...loading, "admin.confirmClientLicenseRenewal": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.confirmClientLicenseRenewal": false });
					return { success: false as const, error: error.message };
				}
			},
			async createTask(task: TNewTask) {
				setLoading({ ...loading, "admin.createTask": true });
				try {
					
					const created = await firebaseApi.firestore.set<TTask>({
						collectionName: "tasks",
						item: task as TTask,
					});
					setLoading({ ...loading, "admin.createTask": false });
					return { success: true, data: created };
				} catch (error: any) {
					setLoading({ ...loading, "admin.createTask": false });
					return { success: false, error: error.message };
				}
			},
			async updateTask(taskId: string, updates: Partial<TTask>) {
				setLoading({ ...loading, "admin.updateTask": true });
				try {
					const withMeta = { id: taskId, ...updates, updatedAt: Date.now() };
					const payload = Object.fromEntries(
						Object.entries(withMeta).filter(([, v]) => v !== undefined),
					) as TTask;
					await firebaseApi.firestore.update<TTask>({
						collectionName: "tasks",
						item: payload,
					});
					setLoading({ ...loading, "admin.updateTask": false });
					return { success: true };
				} catch (error: any) {
					setLoading({ ...loading, "admin.updateTask": false });
					return { success: false, error: error.message };
				}
			},
			async getAdminClientData(clientId: string) {
				setLoading({ ...loading, "admin.getAdminClientData": true });
				try {
					const res = await firebaseApi.firestore.get<TAdminClientData>({
						collectionName: "admin_client_data",
						id: clientId,
					});
					setLoading({ ...loading, "admin.getAdminClientData": false });
					return { success: true, data: res };
				} catch (error: any) {
					setLoading({ ...loading, "admin.getAdminClientData": false });
					return { success: false, error: error.message, data: null as TAdminClientData | null };
				}
			},
			async updateAdminClientData(clientId: string, updates: Partial<TAdminClientData>) {
				setLoading({ ...loading, "admin.updateAdminClientData": true });
				try {
					await firebaseApi.firestore.set<TAdminClientData>({
						collectionName: "admin_client_data",
						item: {
							...updates,
							id: clientId,
							updatedAt: Date.now(),
						} as TAdminClientData,
					});
					setLoading({ ...loading, "admin.updateAdminClientData": false });
					return { success: true };
				} catch (error: any) {
					setLoading({ ...loading, "admin.updateAdminClientData": false });
					return { success: false, error: error.message };
				}
			},
			async appendAdminClientActivity(clientId: string, activity: Omit<TAdminClientActivity, "id" | "createdAt">) {
				setLoading({ ...loading, "admin.appendAdminClientActivity": true });
				try {
					const existing = await firebaseApi.firestore.get<TAdminClientData>({
						collectionName: "admin_client_data",
						id: clientId,
					});
					const entry: TAdminClientActivity = {
						...activity,
						id: crypto.randomUUID(),
						createdAt: Date.now(),
					};
					const next: TAdminClientData = {
						id: clientId,
						adminComment: existing?.adminComment,
						leadSource: existing?.leadSource,
						userActivity: [...(existing?.userActivity ?? []), entry],
						updatedAt: Date.now(),
					};
					await firebaseApi.firestore.set<TAdminClientData>({
						collectionName: "admin_client_data",
						item: next,
					});
					setLoading({ ...loading, "admin.appendAdminClientActivity": false });
					return { success: true, data: entry };
				} catch (error: any) {
					setLoading({ ...loading, "admin.appendAdminClientActivity": false });
					return { success: false, error: error.message };
				}
			},
			async getClientById(clientId: string) {
				setLoading({
					...loading,
					"admin.getClientById": true,
				});
				try {
					const res = await firebaseApi.firestore.get<TProfile>({
						collectionName: "profiles",
						id: clientId,
					});

					setLoading({
						...loading,
						"admin.getClientById": false,
					});

				if (res) {
					return { success: true, data: res };
				} else {
					return { success: false, error: "Client not found" };
				}
			} catch (error: any) {
				setLoading({
					...loading,
					"admin.getClientById": false,
				});
				return { success: false, error: error.message };
			}
		},
			async updateClientProfile(
				clientId: string,
				updates: Partial<
					Pick<
						TProfile,
						| "documents"
						| "status"
						| "firstName"
						| "lastName"
						| "IDNumber"
						| "phone"
						| "phone2"
						| "dob"
						| "sex"
						| "healthInsurance"
						| "city"
						| "street"
						| "houseNumber"
						| "apartmentNumber" | "email"
					>
				>,
			) {
				setLoading({
					...loading,
					"admin.updateClientProfile": true,
				});
				try {
					await firebaseApi.api.updateClientProfile(clientId, updates);
					setLoading({
						...loading,
						"admin.updateClientProfile": false,
					});
					return { success: true };
				} catch (error: any) {
					setLoading({
						...loading,
						"admin.updateClientProfile": false,
					});
					return { success: false, error: error.message };
				}
			},
			async getClientByEmail(email: string) {
				setLoading({
					...loading,
					"admin.getClientByEmail": true,
				});
				try {
					const searchEmail = email.trim().toLowerCase();

					// Try lowercase first (most common case)
					let res = await firebaseApi.firestore.list<TProfile>({
						collectionName: "profiles",
						filter: [{ name: "email", operator: "==", value: searchEmail }],
					});

					// If not found with lowercase, try exact match (original case)
					if (!res || res.length === 0) {
						res = await firebaseApi.firestore.list<TProfile>({
							collectionName: "profiles",
							filter: [{ name: "email", operator: "==", value: email.trim() }],
						});
					}

					setLoading({
						...loading,
						"admin.getClientByEmail": false,
					});

					if (res && res.length > 0) {
						return { success: true, data: res[0] };
					} else {
						return { success: false, error: "לקוח לא נמצא" };
					}
				} catch (error: any) {
					setLoading({
						...loading,
						"admin.getClientByEmail": false,
					});
					return { success: false, error: error.message };
				}
			},
			async getClientByIDNumber(idNumber: string) {
				setLoading({
					...loading,
					"admin.getClientByIDNumber": true,
				});
				try {
					const res = await firebaseApi.firestore.list<TProfile>({
						collectionName: "profiles",
						filter: [{ name: "IDNumber", operator: "==", value: idNumber.trim() }],
					});

					setLoading({
						...loading,
						"admin.getClientByIDNumber": false,
					});

					if (res && res.length > 0) {
						return { success: true, data: res[0] };
					} else {
						return { success: false, error: "לקוח לא נמצא" };
					}
				} catch (error: any) {
					setLoading({
						...loading,
						"admin.getClientByIDNumber": false,
					});
					return { success: false, error: error.message };
				}
			},
			async getClientRequests(clientId: string) {
				setLoading({
					...loading,
					"admin.getClientRequests": true,
				});
				try {
					const res = await firebaseApi.firestore.list<TRequest>({
						collectionName: "requests",
						filter: [{ name: "userId", operator: "==", value: clientId }],
						sort: [{ by: "desc", name: "createdAt" }],
					});

					setLoading({
						...loading,
						"admin.getClientRequests": false,
					});

					return { success: true, data: res };
				} catch (error: any) {
					setLoading({
						...loading,
						"admin.getClientRequests": false,
					});
					return { success: false, error: error.message };
				}
			},
			async getClientPharmacyOrders(clientId: string) {
				setLoading({
					...loading,
					"admin.getClientPharmacyOrders": true,
				});
				try {
					const res = await firebaseApi.firestore.list<TPharmacyOrder>({
						collectionName: "pharmacy_orders",
						filter: [{ name: "userId", operator: "==", value: clientId }],
						sort: [{ by: "desc", name: "createdAt" }],
					});
					// Soft-deleted orders are filtered out here rather than in the query:
					// live orders have no deletedAt field at all, and Firestore cannot
					// match documents on a missing field.
					const visible = res.filter((order) => !order.deletedAt);

					setLoading({
						...loading,
						"admin.getClientPharmacyOrders": false,
					});

					return { success: true, data: visible };
				} catch (error: any) {
					setLoading({
						...loading,
						"admin.getClientPharmacyOrders": false,
					});
					return { success: false, error: error.message };
				}
			},
			async getRequests() {
				setLoading({
					...loading,
					"admin.getRequests": true,
				});
				const res = await firebaseApi.firestore.list({
					collectionName: "requests",
					sort: [{ by: "asc", name: "createdAt" }],
				});

				setLoading({
					...loading,
					"admin.getRequests": false,
				});

				return { success: true, data: res as TRequest[] };
			},
			async getPharmacyOrders() {
				setLoading({
					...loading,
					"admin.getPharmacyOrders": true,
				});
				const res = await firebaseApi.firestore.list({
					collectionName: "pharmacy_orders",
					sort: [{ by: "desc", name: "createdAt" }],
				});

				setLoading({
					...loading,
					"admin.getPharmacyOrders": false,
				});

				// Hide soft-deleted orders from the admin list (see getClientPharmacyOrders).
				const visible = (res as TPharmacyOrder[]).filter((order) => !order.deletedAt);
				return { success: true, data: visible };
			},
			async getPharmacyOrder(id: string) {
				setLoading({
					...loading,
					"admin.getPharmacyOrder": true,
				});
				const res = await firebaseApi.firestore.get({
					collectionName: "pharmacy_orders",
					id,
				});

				setLoading({
					...loading,
					"admin.getPharmacyOrder": false,
				});

				return { success: true, data: res as TPharmacyOrder };
			},
			async deletePharmacyOrder(orderId: string) {
				setLoading({ ...loading, "admin.deletePharmacyOrder": true });
				try {
					const res = await firebaseApi.api.deletePharmacyOrder({ orderId });
					setLoading({ ...loading, "admin.deletePharmacyOrder": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.deletePharmacyOrder": false });
					return { success: false, error: error.message };
				}
			},
			async updatePharmacyOrderStatus({
				id,
				status,
			}: {
				id: string;
				status: TPharmacyOrderStatus;
			}) {
				setLoading({
					...loading,
					"admin.updatePharmacyOrderStatus": true,
				});

				const res = await firebaseApi.firestore.set<Partial<TPharmacyOrder>>({
					collectionName: "pharmacy_orders",
					item: { id, status, updatedAt: Date.now() },
				});

				setLoading({
					...loading,
					"admin.updatePharmacyOrderStatus": false,
				});

				return { success: true, data: res as TPharmacyOrder };
			},
			async generatePaymentLink(data: { request: TRequest; amount: number; title: string }) {
				setLoading({
					...loading,
					"admin.generatePaymentLink": true,
				});

				try {
					const response = await firebaseApi.api.generatePaymentLink(data);

					setLoading({
						...loading,
						"admin.generatePaymentLink": false,
					});

					return response;
				} catch (error: any) {
					setLoading({
						...loading,
						"admin.generatePaymentLink": false,
					});
					return { success: false, paymentLink: "", errMessage: error.message };
				}
			},

			async getCoinsConfig() {
				return firebaseApi.api.getCoinsConfig();
			},

			async adjustUserCoins(userId: string, delta: number, reason: string) {
				setLoading({ ...loading, "admin.adjustUserCoins": true });
				try {
					const res = await firebaseApi.api.adminAdjustCoins({ userId, delta, reason });
					setLoading({ ...loading, "admin.adjustUserCoins": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.adjustUserCoins": false });
					return { success: false, error: error.message };
				}
			},

			async updateCoinsConfig(config: { pharmacyEarnPercent: number; requestEarnPercent: number }) {
				setLoading({ ...loading, "admin.updateCoinsConfig": true });
				try {
					const res = await firebaseApi.api.updateCoinsConfig(config);
					setLoading({ ...loading, "admin.updateCoinsConfig": false });
					return res;
				} catch (error: any) {
					setLoading({ ...loading, "admin.updateCoinsConfig": false });
					return { success: false, error: error.message };
				}
			},
		},

		system: {
			uiLogs(payload: LogPayload) {
				logger(payload);
			},
			logout: async () => {
				await firebaseApi.auth.logout();
				store.storeApi.setUser(null);
				navigate("/login");
			},
			login: async ({ email, password }: { email: string; password: string }) => {
				try {
					const res = await firebaseApi.auth.login(email, password);
					logger({
						message: `login success (${email})`,
						severity: "INFO",
						userId: res.user?.uid,
						email,
					});
					analytics.track("LOGIN", { email });
					return { user: res.user, success: true, error: null };
				} catch (error: any) {
					logger({
						message: `login error (${error.message})`,
						severity: "ERROR",
						email,
						error: error as FirebaseError,
					});
					return { success: false, error: error as FirebaseError, user: null };
				}
			},
			requestPhoneCode: async (phone: string, recaptchaVerifier: RecaptchaVerifier) => {
				console.log("requestPhoneCode", phone, recaptchaVerifier);
				try {
					const confirmationResult = await firebaseApi.auth.signInWithPhone(
						phone,
						recaptchaVerifier,
					);
					console.log("confirmationResult", confirmationResult);
					logger({
						message: `phone code sent (${phone})`,
						severity: "INFO",
						phone,
					});
					return { success: true, confirmationResult, error: null };
				} catch (error: any) {
					console.log("error", error);
					logger({
						message: `phone code request error (${error.message})`,
						severity: "ERROR",
						phone,
						error: error as FirebaseError,
					});
					return { success: false, confirmationResult: null, error: error as FirebaseError };
				}
			},
			confirmPhoneCode: async (confirmationResult: any, code: string) => {
				try {
					const res = await firebaseApi.auth.confirmPhoneCode(confirmationResult, code);
					logger({
						message: `phone login success`,
						severity: "INFO",
						userId: res.user?.uid,
					});
					return { user: res.user, success: true, error: null };
				} catch (error: any) {
					logger({
						message: `phone code confirmation error (${error.message})`,
						severity: "ERROR",
						error: error as FirebaseError,
					});
					return { success: false, error: error as FirebaseError, user: null };
				}
			},
		},

		doctor: {
			async approveRequest({
				files,
				id,
				approveMessageByDoctor,
			}: {
				files: File[];
				id: string;
				approveMessageByDoctor?: string;
			}) {
				setLoading({
					...loading,
					"doctor.approveRequest": true,
				});

				const filesToUploud: Promise<{
					success?: boolean;
					name?: string;
					folder?: string;
					fullUrl?: string;
					contentType?: string;
					errorMessage?: undefined;
					extension?: string;
					size: number;
				}>[] = [];

				const uploadIds = files.map(() => crypto.randomUUID());
				files.forEach((file, index) => {
					filesToUploud.push(
						firebaseApi.storage.uploadFile({
							file: file,
							name: file.name,
							// Unique per upload: a per-index folder made the second approval
							// overwrite the first approval's files at the same Storage path.
							folder: `requests/${id}/doctor/${uploadIds[index]}`,
							type: "CUSTOM",
						}),
					);
				});

				const uploadedFiles = await Promise.all(filesToUploud);

				const doctorDocuments: TRequest["doctorDocuments"] = uploadedFiles.map(
					(file, index) => {
						return {
							createdAt: Date.now(),
							extension: (file.extension as any) ?? "",
							fileType: (file.contentType as any) ?? "",
							name: file.name ?? "",
							size: file.size,
							type: `CUSTOM${index + 1}` as any,
							url: file.fullUrl ?? "",
							path: `requests/${id}/doctor/${uploadIds[index]}/${file.name}`,
							label: file.name ?? "",
							// todo add extension
						};
					},
				);
				// arrayUnion, not a plain array: firestore.set merges, and a merged
				// array field is REPLACED wholesale. Assigning here would erase any
				// recommendation the doctor had already generated from a template.
				const res = await firebaseApi.firestore.set<Partial<TRequest>>({
					collectionName: "requests",
					item: {
						id,
						status: "approved_by_doctor",
						...(doctorDocuments.length > 0 && {
							doctorDocuments: arrayUnion(...doctorDocuments) as never,
						}),
						...(approveMessageByDoctor && { approveMessageByDoctor }),
					},
				});

				setLoading({
					...loading,
					"doctor.approveRequest": false,
				});

				return { success: true, data: res };
			},
			async rejectRequest({ message, id }: { message: string; id: string }) {
				setLoading({
					...loading,
					"doctor.rejectRequest": true,
				});
				const res = await firebaseApi.firestore.set<Partial<TRequest>>({
					collectionName: "requests",
					item: { id, status: "rejected_by_doctor", rejectMessageByDoctor: message },
				});

				setLoading({
					...loading,
					"doctor.rejectRequest": false,
				});

				return { success: true, data: res };
			},
			async getRequests() {
				setLoading({
					...loading,
					"client.getRequests": true,
				});
				const res = await firebaseApi.firestore.list<TRequest>({
					collectionName: "requests",
					filter: [{ name: "doctor.id", operator: "==", value: user?.uid }],
					sort: [{ by: "asc", name: "createdAt" }],
				});

				setLoading({
					...loading,
					"client.getRequests": false,
				});

				return res as TRequest[];
			},
			templates: {
				async list(): Promise<TDocumentTemplate[]> {
					const res = await firebaseApi.firestore.list<TDocumentTemplate>({
						collectionName: "document_templates",
						filter: [{ name: "doctorId", operator: "==", value: user?.uid }],
						sort: [{ by: "desc", name: "updatedAt" }],
					});
					return res;
				},
				async get(id: string): Promise<TDocumentTemplate | null> {
					return firebaseApi.firestore.get<TDocumentTemplate>({
						collectionName: "document_templates",
						id,
					});
				},
				async create(data: TNewDocumentTemplate): Promise<TDocumentTemplate> {
					if (!user?.uid) throw new Error("User not authenticated");
					const now = Date.now();
					const template: TDocumentTemplate = {
						...data,
						id: crypto.randomUUID(),
						doctorId: user.uid,
						createdAt: now,
						updatedAt: now,
					};
					const res = await firebaseApi.firestore.set<TDocumentTemplate>({
						collectionName: "document_templates",
						item: template,
					});
					return res as TDocumentTemplate;
				},
				async update(id: string, data: Partial<TNewDocumentTemplate>): Promise<void> {
					await firebaseApi.firestore.update<Partial<TDocumentTemplate> & { id: string }>({
						collectionName: "document_templates",
						item: { ...data, id, updatedAt: Date.now() },
					});
				},
				async delete(id: string): Promise<void> {
					await firebaseApi.firestore.delete({
						collectionName: "document_templates",
						id,
					});
				},
			},
			async uploadSignature(file: File): Promise<void> {
				if (!user?.uid) throw new Error("User not authenticated");

				const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
				if (!allowed.includes(file.type)) {
					throw new Error("סוג קובץ לא נתמך. יש להעלות PNG, JPG או WebP בלבד");
				}

				if (file.size > 1024 * 1024) {
					throw new Error("הקובץ גדול מדי. הגודל המרבי הוא 1MB");
				}

				const mimeToExt: Record<string, string> = {
					"image/png": "png",
					"image/jpeg": "jpg",
					"image/jpg": "jpg",
					"image/webp": "webp",
				};
				const ext = mimeToExt[file.type] ?? "png";
				const folder = `doctors/${user.uid}`;
				const name = `signature.${ext}`;

				const result = await firebaseApi.storage.uploadFile({
					folder,
					name,
					file,
					type: "SIGNATURE",
				});

				if (!result.success || !result.fullUrl) {
					throw new Error(result.errorMessage ?? "Upload failed");
				}

				const url = result.fullUrl;
				const signaturePath = `${folder}/${name}`;

				// Validate that the returned URL is a proper Firebase Storage HTTPS URL
				if (!url || !url.startsWith("https://firebasestorage.googleapis.com")) {
					throw new Error("כתובת החתימה אינה תקינה");
				}

				// Validate the path matches expected pattern
				const pathRegex = /^doctors\/[a-zA-Z0-9_-]+\/signature\.(png|jpg|jpeg|webp)$/;
				if (!pathRegex.test(signaturePath)) {
					throw new Error("נתיב החתימה אינו תקין");
				}

				await firebaseApi.firestore.update<TDoctor>({
					collectionName: "doctors",
					item: {
						id: user.uid,
						signatureUrl: url,
						signaturePath,
						signatureUploadedAt: Date.now(),
					} as TDoctor,
				});
			},
			async deleteSignature(): Promise<void> {
				if (!user?.uid) throw new Error("User not authenticated");

				const doctor = await firebaseApi.firestore.get<TDoctor>({
					collectionName: "doctors",
					id: user.uid,
				});

				// Clear Firestore fields first so the reference is gone even if storage delete fails
				await firebaseApi.firestore.update<Partial<TDoctor> & { id: string }>({
					collectionName: "doctors",
					item: {
						id: user.uid,
						signatureUrl: null,
						signaturePath: null,
						signatureUploadedAt: null,
					} as unknown as Partial<TDoctor> & { id: string },
				});

				// Best-effort delete from Storage — log failure but don't throw
				if (doctor?.signaturePath) {
					try {
						await firebaseApi.storage.remove(doctor.signaturePath);
					} catch (err) {
						console.error("Failed to delete signature from storage (non-fatal):", err);
					}
				}
			},
			async addDocumentToRequest(requestId: string, document: TDocument): Promise<void> {
				const db = getFirestore(app);
				const ref = doc(db, "requests", requestId);
				await updateDoc(ref, {
					doctorDocuments: arrayUnion(document),
				});
			},
			async generateDocumentFromTemplate(params: {
				requestId: string;
				templateId: string;
				content: TDocumentContent;
				title: string;
				signaturePosition: TSignaturePosition;
			}): Promise<void> {
				// The cloud function generates the PDF and atomically writes it to Firestore.
				// The client must not construct or submit a TDocument — doing so would allow
				// a malicious actor to substitute a different URL or path.
				// JSON round-trip strips null-prototype objects produced by ProseMirror/TipTap
				// getJSON(). Without this, Firebase's httpsCallable serializer throws
				// "o.hasOwnProperty is not a function".
				const safeContent = JSON.parse(JSON.stringify(params.content)) as object;
				const result = await firebaseApi.api.renderDocumentToPdf({
					requestId: params.requestId,
					templateId: params.templateId,
					content: safeContent,
					title: params.title,
					signaturePosition: params.signaturePosition,
				});

				const data = result.data;
				if (!data.success) {
					throw new Error("שגיאה ביצירת המסמך");
				}
			},
		},

		broker: {
			async getRequests() {
				setLoading({
					...loading,
					"broker.getRequests": true,
				});
				const res = await firebaseApi.firestore.list<TRequest>({
					collectionName: "requests",
					filter: [{ name: "brokerId", operator: "==", value: user?.uid }],
					sort: [{ by: "asc", name: "createdAt" }],
				});

				setLoading({
					...loading,
					"broker.getRequests": false,
				});

				return res as TRequest[];
			},
			async getRequest(id: string) {
				setLoading({
					...loading,
					"broker.getRequest": true,
				});
				const res = await firebaseApi.firestore.get({
					collectionName: "requests",
					id,
				});

				setLoading({
					...loading,
					"broker.getRequest": false,
				});

				return { success: true, data: res as TRequest };
			},
			async setRequest(data: Partial<TRequest>) {
				const res = await firebaseApi.firestore.set({
					collectionName: "requests",
					item: {
						...data,
						brokerId: user?.uid,
						createdBy: "broker",
					},
				});
				return { success: true, request: res };
			},
		},
	};
	return { api, loading, navigate, goBack };
};
